import { useEffect, useState } from 'react';
import { clienteService, prestamoService } from '../../services/api';
import '../../styles/Reportes.css';

const Incobrables = () => {
  const [prestamos, setPrestamos] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [diasAtraso, setDiasAtraso] = useState(30);

  useEffect(() => {
    cargarIncobrables();
  }, []);

  const cargarIncobrables = async () => {
    setCargando(true);
    try {
      const [responsePrestamos, responseClientes] = await Promise.all([
        prestamoService.getAll(),
        clienteService.getAll()
      ]);

      const todosPrestamos = responsePrestamos.data.prestamos || [];
      const todosClientes = responseClientes.data.clientes || [];

      // Crear mapa de clientes para búsqueda rápida
      const mapaClientes = todosClientes.reduce((acc, c) => {
        acc[c.id] = c;
        return acc;
      }, {});

      // Filtrar préstamos problemáticos
      const hoy = new Date();
      const incobrables = [];

      for (const prestamo of todosPrestamos) {
        if (prestamo.estado === 'pagado') continue;

        // Calcular días de atraso
        const fechaInicio = new Date(prestamo.fecha_inicio);
        const diasTranscurridos = Math.floor((hoy - fechaInicio) / (1000 * 60 * 60 * 24));
        const cuotasEsperadas = Math.floor(diasTranscurridos / (prestamo.modalidad === 'diario' ? 1 : prestamo.modalidad === 'semanal' ? 7 : 30));
        const cuotasAtrasadas = Math.max(0, cuotasEsperadas - prestamo.cuotas_pagadas);
        const diasAtrasoCalc = prestamo.modalidad === 'diario' ? cuotasAtrasadas :
                               prestamo.modalidad === 'semanal' ? cuotasAtrasadas * 7 :
                               cuotasAtrasadas * 30;

        // Incluir si está atrasado más de X días o está vencido
        if (diasAtrasoCalc >= diasAtraso || prestamo.estado === 'vencido') {
          const cliente = mapaClientes[prestamo.cliente_id] || {};
          incobrables.push({
            ...prestamo,
            cliente_nombre: `${cliente.nombre || ''} ${cliente.apellido || ''}`.trim() || 'Desconocido',
            cliente_telefono: cliente.telefono || 'N/A',
            cuotas_atrasadas: cuotasAtrasadas,
            dias_atraso: diasAtrasoCalc
          });
        }
      }

      // Ordenar por días de atraso descendente
      incobrables.sort((a, b) => b.dias_atraso - a.dias_atraso);
      setPrestamos(incobrables);
    } catch (error) {
      alert('Error al cargar préstamos incobrables');
      console.error(error);
    } finally {
      setCargando(false);
    }
  };

  const calcularTotales = () => {
    const totalPrestado = prestamos.reduce((sum, p) => sum + p.monto_prestado, 0);
    const totalEnRiesgo = prestamos.reduce((sum, p) => {
      const saldo = p.total - (p.cuotas_pagadas * (p.total / p.cuotas));
      return sum + Math.max(0, saldo);
    }, 0);
    const totalCuotasAtrasadas = prestamos.reduce((sum, p) => sum + p.cuotas_atrasadas, 0);

    return { totalPrestado, totalEnRiesgo, totalCuotasAtrasadas };
  };

  const totales = calcularTotales();

  const formatearFecha = (fecha) => {
    return new Date(fecha).toLocaleDateString('es-DO');
  };

  const getSeveridad = (dias) => {
    if (dias >= 90) return { clase: 'badge-vencido', texto: 'Crítico' };
    if (dias >= 60) return { clase: 'badge-atrasado', texto: 'Alto' };
    if (dias >= 30) return { clase: 'badge-pendiente', texto: 'Medio' };
    return { clase: 'badge-default', texto: 'Bajo' };
  };

  return (
    <div className="reporte-detalle">
      <div className="reporte-header">
        <h1>⚠️ Préstamos Incobrables</h1>
        <p>Préstamos con alto riesgo de no ser recuperados</p>
      </div>

      <div className="filtros-container">
        <div className="filtro-grupo">
          <label>Días Mínimos de Atraso:</label>
          <input
            type="number"
            value={diasAtraso}
            onChange={(e) => setDiasAtraso(Number(e.target.value))}
            className="input-text"
            min="1"
          />
        </div>

        <button onClick={cargarIncobrables} className="btn-buscar">
          Actualizar
        </button>
      </div>

      {cargando && (
        <div className="loading">Analizando cartera...</div>
      )}

      {!cargando && prestamos.length > 0 && (
        <>
          <div className="resumen-cards">
            <div className="card-resumen" style={{ borderLeft: '4px solid #e74c3c' }}>
              <div className="card-icono">⚠️</div>
              <div className="card-info">
                <div className="card-valor" style={{ color: '#e74c3c' }}>
                  {prestamos.length}
                </div>
                <div className="card-label">Préstamos en Riesgo</div>
              </div>
            </div>

            <div className="card-resumen" style={{ borderLeft: '4px solid #e67e22' }}>
              <div className="card-icono">💰</div>
              <div className="card-info">
                <div className="card-valor" style={{ color: '#e67e22' }}>
                  ${totales.totalPrestado.toFixed(2)}
                </div>
                <div className="card-label">Capital Prestado</div>
              </div>
            </div>

            <div className="card-resumen" style={{ borderLeft: '4px solid #c0392b' }}>
              <div className="card-icono">📉</div>
              <div className="card-info">
                <div className="card-valor" style={{ color: '#c0392b' }}>
                  ${totales.totalEnRiesgo.toFixed(2)}
                </div>
                <div className="card-label">Saldo en Riesgo</div>
              </div>
            </div>

            <div className="card-resumen" style={{ borderLeft: '4px solid #8e44ad' }}>
              <div className="card-icono">�</div>
              <div className="card-info">
                <div className="card-valor" style={{ color: '#8e44ad' }}>
                  {totales.totalCuotasAtrasadas}
                </div>
                <div className="card-label">Cuotas Atrasadas</div>
              </div>
            </div>
          </div>

          <div className="tabla-container">
            <table className="tabla-reporte">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Cliente</th>
                  <th>Teléfono</th>
                  <th>Monto</th>
                  <th>Total</th>
                  <th>Cuotas</th>
                  <th>Atrasadas</th>
                  <th>Días Atraso</th>
                  <th>Severidad</th>
                  <th>Estado</th>
                  <th>Fecha</th>
                </tr>
              </thead>
              <tbody>
                {prestamos.map(prestamo => {
                  const severidad = getSeveridad(prestamo.dias_atraso);
                  return (
                    <tr key={prestamo.id}>
                      <td>{prestamo.id}</td>
                      <td><strong>{prestamo.cliente_nombre}</strong></td>
                      <td>{prestamo.cliente_telefono}</td>
                      <td>${prestamo.monto_prestado?.toFixed(2)}</td>
                      <td>${prestamo.total?.toFixed(2)}</td>
                      <td>{prestamo.cuotas_pagadas}/{prestamo.cuotas}</td>
                      <td className="destacado"><strong>{prestamo.cuotas_atrasadas}</strong></td>
                      <td style={{ color: '#e74c3c', fontWeight: 'bold' }}>{prestamo.dias_atraso}</td>
                      <td>
                        <span className={`badge ${severidad.clase}`}>
                          {severidad.texto}
                        </span>
                      </td>
                      <td>
                        <span className={`badge badge-${prestamo.estado}`}>
                          {prestamo.estado}
                        </span>
                      </td>
                      <td>{formatearFecha(prestamo.fecha_inicio)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="info-prestamo" style={{ marginTop: '20px', background: '#ffebee' }}>
            <h3 style={{ color: '#c62828' }}>📋 Recomendaciones</h3>
            <ul style={{ color: '#d32f2f', lineHeight: '1.8' }}>
              <li>Contactar inmediatamente a clientes con más de 60 días de atraso</li>
              <li>Evaluar restructuración de deuda para casos críticos</li>
              <li>Considerar acciones legales para préstamos con más de 90 días de atraso</li>
              <li>Implementar plan de cobro intensivo</li>
            </ul>
          </div>
        </>
      )}

      {!cargando && prestamos.length === 0 && (
        <div className="sin-datos">
          <p>✅ No hay préstamos incobrables con más de {diasAtraso} días de atraso</p>
        </div>
      )}
    </div>
  );
};

export default Incobrables;
